import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useBrandSearch } from '@/composables/useBrandSearch'
import { i18n } from '@/i18n'
import { candidateDomainKey } from '@/lib/competition/candidate-domain'
import { pickedCandidates, seedPicks, withDirectCandidate } from '@/lib/competition/candidate-picks'
import { parseCallableError } from '@/lib/firebase/errors'
import {
  COMPETITORS_NAVIGATOR,
  type CompetitorsDiscoveryState,
  type CompetitorsMove,
  type CompetitorsState,
  competitorsPhase,
  resumeCompetitorsScreen,
} from '@/lib/setup/competitors-navigation'
import { screenIndex } from '@/lib/setup/setup-screen'
import { CONFIRM_STAMP_WAIT_MS, whenChanged } from '@/lib/setup/when-changed'
import {
  confirmCompetitors as repoConfirmCompetitors,
  discoverCompetitors as repoDiscoverCompetitors,
  isCompetitorDiscoveryRunInProgressReason,
  subscribeCompetitors as repoSubscribeCompetitors,
  subscribeLatestCompetitorCandidateRun as repoSubscribeLatestCompetitorCandidateRun,
} from '@/repositories/competition'
import { isOnboardingLaterStepRunningReason } from '@/repositories/setup'
import { useBrandStore } from '@/stores/brand'
import { useSetupPositionStore } from '@/stores/setup-position'
import {
  currentSetupScope,
  useLatestRun,
  useScopedSubscription,
} from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import {
  type BrandCandidate,
  COMPETITOR_DISCOVERY_RUN_STALE_AFTER_MS,
  type CompetitorCandidate,
  type CompetitorCandidateGroup,
  type CompetitorCandidateRun,
  type KnownCompetitor,
  MAX_PICKED_COMPETITORS,
} from '@/types/competition'
import type { Activity } from '@/types/activity'
import { isError, isLoaded } from '@/types/load-state'
import { type StepConfirmation, isCurrentSince } from '@/types/onboarding-chain'
import { isRunFailed, isRunLive, runActivity } from '@/types/setup-run'

export type AddCandidateOutcome = 'added' | 'ticked' | 'picked' | 'limit'

export interface LastCompetitorAdd {
  readonly outcome: 'added' | 'ticked'
  readonly key: string
  readonly name: string
  readonly group: CompetitorCandidateGroup | null
}

export type StartDiscoveryOutcome =
  | { readonly status: 'started'; readonly runId: string }
  | { readonly status: 'refused'; readonly reason: string | undefined }

export const useCompetitorsSetupStore = defineStore('setup-competitors', () => {
  const runSubscription = useLatestRun<CompetitorCandidateRun>(
    repoSubscribeLatestCompetitorCandidateRun,
    COMPETITOR_DISCOVERY_RUN_STALE_AFTER_MS,
  )
  const competitorsSubscription = useScopedSubscription(repoSubscribeCompetitors)

  const latestRun = runSubscription.state
  const run = runSubscription.run
  const competitors = competitorsSubscription.state

  const calling = ref(false)
  const awaitedRunId = ref<string | null>(null)
  const startFailed = ref(false)
  const named = ref<readonly BrandCandidate[]>([])

  const candidates = ref<readonly CompetitorCandidate[]>([])
  const picks = ref<ReadonlySet<string>>(new Set())
  const limitReached = ref(false)
  const lastAdd = ref<LastCompetitorAdd | null>(null)
  const edited = ref(false)
  const seededFromRunId = ref<string | null>(null)

  const { query, results, searching, searchError, searchedQuery, search, clearSearch } =
    useBrandSearch()

  const confirming = ref(false)

  function open(): void {
    const scope = currentSetupScope()
    if (!scope) {
      const message = i18n.global.t('common.noWorkspaceSelected')
      runSubscription.fail(message)
      competitorsSubscription.fail(message)
      return
    }
    runSubscription.open(scope)
    competitorsSubscription.open(scope)
  }

  function close(): void {
    runSubscription.close()
    competitorsSubscription.close()
    awaitedRunId.value = null
    startFailed.value = false
    named.value = []
    candidates.value = []
    picks.value = new Set()
    limitReached.value = false
    lastAdd.value = null
    edited.value = false
    seededFromRunId.value = null
    clearSearch()
  }

  watch(query, (typed) => {
    if (typed !== '') lastAdd.value = null
  })

  const justAddedKey = computed(() => lastAdd.value?.key ?? null)

  function clearLastAdd(): void {
    lastAdd.value = null
  }

  const resolved = computed(
    () => latestRun.value.status !== 'loading' && competitors.value.status !== 'loading',
  )

  const readFailed = computed(() => isError(latestRun.value) || isError(competitors.value))

  const tracked = computed(() => (isLoaded(competitors.value) ? competitors.value.value : []))

  const factsConfirmedAt = computed(() => {
    const facts = useBrandStore().brandFacts
    return isLoaded(facts) ? facts.value.meta.confirmedAt : null
  })

  const confirmedAt = computed(() => {
    const brand = useBrandStore().brand
    return isLoaded(brand) ? brand.value.competitorsConfirmedAt : null
  })

  const discoveryCurrent = computed(
    () => run.value !== null && isCurrentSince(run.value.createdAt, factsConfirmedAt.value),
  )

  const currentRun = computed(() => (discoveryCurrent.value ? run.value : null))

  const discoveryState = computed<CompetitorsDiscoveryState>(() => {
    const latest = run.value
    if (latest === null) return 'none'
    if (isRunLive(latest)) return 'live'
    return isRunFailed(latest) ? 'failed' : 'settled'
  })

  const state = computed<CompetitorsState>(() => ({
    trackedCount: tracked.value.length,
    discovery: discoveryState.value,
    confirmedCurrent: isCurrentSince(confirmedAt.value, factsConfirmedAt.value),
    discoveryCurrent: discoveryCurrent.value,
  }))

  const phase = computed(() => competitorsPhase(state.value))

  const confirmation = computed<StepConfirmation>(() => ({
    confirmed: state.value.trackedCount > 0,
    confirmedAt: confirmedAt.value,
  }))

  watch(run, (latest) => {
    if (latest !== null && latest.id === awaitedRunId.value) awaitedRunId.value = null
  })

  const starting = computed(() => calling.value || awaitedRunId.value !== null)

  const isFinding = runSubscription.isLive

  const discoveryFailed = computed(
    () =>
      !starting.value &&
      (startFailed.value || (currentRun.value !== null && isRunFailed(currentRun.value))) &&
      (phase.value === 'choose' || phase.value === 'finding'),
  )

  function resumeIndex(): number {
    return screenIndex(COMPETITORS_NAVIGATOR.screens, resumeCompetitorsScreen(state.value))
  }

  function move(applied: CompetitorsMove): void {
    useSetupPositionStore().move('competitors', COMPETITORS_NAVIGATOR, applied)
  }

  /** Back on the list: the brand facts sheet while the step is `locked`, else the choice. */
  function back(locked: boolean): void {
    if (locked) useSetupPositionStore().backToPreviousStep()
    else move('back-to-known')
  }

  /** Tracks the named companies and starts a discovery that looks for more; Try again sends the same names. */
  async function startDiscovery(
    knownCompetitors: readonly KnownCompetitor[] = named.value,
  ): Promise<StartDiscoveryOutcome> {
    calling.value = true
    startFailed.value = false
    edited.value = false
    try {
      const { runId } = await repoDiscoverCompetitors(
        knownCompetitors.map(({ name, websiteUrl }) => ({ name, websiteUrl })),
      )
      if (run.value?.id !== runId) awaitedRunId.value = runId
      return { status: 'started', runId }
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (!isCompetitorDiscoveryRunInProgressReason(parsed.reason)) {
        startFailed.value = true
        useToastStore().error(i18n.global.t('setup.errors.startFailed'), parsed.message)
      }
      return { status: 'refused', reason: parsed.reason }
    } finally {
      calling.value = false
    }
  }

  /** Adds a company to the named list; `'limit'` once four are named. */
  function addNamed(candidate: BrandCandidate): 'added' | 'picked' | 'limit' {
    const key = candidateDomainKey(candidate.domain)
    if (named.value.some((kept) => candidateDomainKey(kept.domain) === key)) return 'picked'
    if (named.value.length >= MAX_PICKED_COMPETITORS) return 'limit'
    named.value = [...named.value, candidate]
    return 'added'
  }

  /** Drops a company from the named list by its domain key. */
  function removeNamed(key: string): void {
    named.value = named.value.filter((kept) => candidateDomainKey(kept.domain) !== key)
  }

  /** Forgets the named list, for a discovery with no names. */
  function clearNamed(): void {
    named.value = []
  }

  watch(
    [run, () => competitors.value.status !== 'loading'],
    ([latest, trackedRead]) => {
      if (!trackedRead || edited.value || latest === null) return
      if (isRunLive(latest) || isRunFailed(latest) || latest.id === seededFromRunId.value) return
      seededFromRunId.value = latest.id
      const seeded = seedPicks(latest.candidates, tracked.value, MAX_PICKED_COMPETITORS)
      candidates.value = seeded.candidates
      picks.value = seeded.picks
      limitReached.value = false
    },
    { immediate: true },
  )

  const picked = computed(() => pickedCandidates(candidates.value, picks.value))

  function isListed(key: string): boolean {
    return candidates.value.some((candidate) => candidateDomainKey(candidate.domain) === key)
  }

  function setPick(key: string, ticked: boolean): void {
    const next = new Set(picks.value)
    if (ticked) next.add(key)
    else next.delete(key)
    picks.value = next
    limitReached.value = false
    lastAdd.value = null
    edited.value = true
  }

  function toggleCandidate(domain: string): void {
    const key = candidateDomainKey(domain)
    if (!isListed(key)) return
    if (picks.value.has(key)) {
      setPick(key, false)
      return
    }
    if (picks.value.size >= MAX_PICKED_COMPETITORS) {
      limitReached.value = true
      return
    }
    setPick(key, true)
  }

  function addCandidate(candidate: BrandCandidate): AddCandidateOutcome {
    const key = candidateDomainKey(candidate.domain)
    const listed = candidates.value.find((kept) => candidateDomainKey(kept.domain) === key)
    if (listed !== undefined && picks.value.has(key)) return 'picked'
    if (picks.value.size >= MAX_PICKED_COMPETITORS) {
      limitReached.value = true
      return 'limit'
    }
    if (listed === undefined) candidates.value = withDirectCandidate(candidates.value, candidate)
    setPick(key, true)
    const outcome = listed === undefined ? 'added' : 'ticked'
    lastAdd.value = {
      outcome,
      key,
      name: listed?.name ?? candidate.name,
      group: listed?.group ?? null,
    }
    return outcome
  }

  async function confirm(): Promise<boolean> {
    if (picked.value.length === 0 || confirming.value) return false
    confirming.value = true
    try {
      const before = confirmedAt.value
      await repoConfirmCompetitors(
        picked.value.map(({ name, websiteUrl, reason }) => ({ name, websiteUrl, reason })),
      )
      await whenChanged(() => confirmedAt.value, before, CONFIRM_STAMP_WAIT_MS)
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (isOnboardingLaterStepRunningReason(parsed.reason)) {
        useToastStore().error(i18n.global.t('setup.locked.laterStepRunning'))
      } else {
        useToastStore().error(i18n.global.t('setup.errors.confirmFailed'), parsed.message)
      }
      return false
    } finally {
      confirming.value = false
    }
  }

  const activity = computed<Activity>(() => {
    switch (phase.value) {
      case 'choose':
        return 'idle'
      case 'finding':
      case 'review':
        return runActivity(run.value)
      case 'done':
        return 'done'
    }
  })

  return {
    latestRun,
    run,
    currentRun,
    competitors,
    tracked,
    named,
    phase,
    isFinding,
    starting,
    startFailed,
    discoveryFailed,
    candidates,
    seededFromRunId,
    picks,
    picked,
    limitReached,
    lastAdd,
    justAddedKey,
    edited,
    query,
    results,
    searching,
    searchError,
    searchedQuery,
    confirming,
    resolved,
    readFailed,
    confirmation,
    activity,
    open,
    close,
    resumeIndex,
    move,
    back,
    startDiscovery,
    addNamed,
    removeNamed,
    clearNamed,
    toggleCandidate,
    addCandidate,
    clearLastAdd,
    search,
    clearSearch,
    confirm,
  }
})
